/* global axios */

const normalizeBase = apiUrl => {
  if (!apiUrl) return '';
  const trimmed = apiUrl.replace(/\/+$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
};

class WahaAPI {
  static requestHeaders(adminToken) {
    return {
      'X-Api-Key': adminToken,
      'Content-Type': 'application/json',
    };
  }

  static async listSessions(
    apiUrl,
    adminToken,
    { includeStopped = false } = {}
  ) {
    if (!apiUrl || !adminToken) {
      return Promise.reject(new Error('Missing Waha credentials'));
    }

    const url = `${normalizeBase(apiUrl)}/sessions`;

    try {
      const response = await axios.get(url, {
        headers: this.requestHeaders(adminToken),
        params: includeStopped ? { all: true } : {},
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || 'Failed to fetch Waha sessions'
        );
      }
      throw new Error('Network error while fetching Waha sessions');
    }
  }

  static async deleteSession(apiUrl, adminToken, sessionName) {
    if (!apiUrl || !adminToken || !sessionName) {
      return Promise.reject(new Error('Missing Waha credentials'));
    }

    const url = `${normalizeBase(apiUrl)}/sessions/${sessionName}`;

    try {
      const response = await axios.delete(url, {
        headers: this.requestHeaders(adminToken),
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || 'Failed to delete Waha session'
        );
      }
      throw new Error('Network error while deleting Waha session');
    }
  }

  static async logoutSession(apiUrl, adminToken, sessionName) {
    if (!apiUrl || !adminToken || !sessionName) {
      return Promise.reject(new Error('Missing Waha credentials'));
    }

    const url = `${normalizeBase(apiUrl)}/sessions/logout`;

    try {
      const response = await axios.post(
        url,
        { name: sessionName },
        {
          headers: {
            ...this.requestHeaders(adminToken),
            Accept: 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || 'Failed to log out Waha session'
        );
      }
      throw new Error('Network error while logging out Waha session');
    }
  }

  static async stopSession(apiUrl, adminToken, sessionName, logout = false) {
    if (!apiUrl || !adminToken || !sessionName) {
      return Promise.reject(new Error('Missing Waha credentials'));
    }

    const url = `${normalizeBase(apiUrl)}/sessions/stop`;

    try {
      const response = await axios.post(
        url,
        { name: sessionName, logout },
        {
          headers: {
            ...this.requestHeaders(adminToken),
            Accept: 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || 'Failed to stop Waha session'
        );
      }
      throw new Error('Network error while stopping Waha session');
    }
  }

  static async restartSession(apiUrl, adminToken, sessionName) {
    if (!apiUrl || !adminToken || !sessionName) {
      return Promise.reject(new Error('Missing Waha credentials'));
    }

    const url = `${normalizeBase(apiUrl)}/sessions/${sessionName}/restart`;

    try {
      const response = await axios.post(
        url,
        {},
        {
          headers: {
            ...this.requestHeaders(adminToken),
            Accept: 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || 'Failed to restart Waha session'
        );
      }
      throw new Error('Network error while restarting Waha session');
    }
  }

  static async fetchQrCode(apiUrl, adminToken, sessionName) {
    if (!apiUrl || !adminToken || !sessionName) {
      return Promise.reject(new Error('Missing Waha credentials'));
    }

    const url = `${normalizeBase(apiUrl)}/${sessionName}/auth/qr`;

    try {
      const response = await axios.get(url, {
        headers: {
          ...this.requestHeaders(adminToken),
          Accept: 'image/png',
        },
        params: { format: 'raw' },
        responseType: 'json',
      });
      const payload = response.data;

      if (typeof payload === 'string') {
        return payload;
      }

      if (payload && typeof payload === 'object') {
        return (
          payload.base64 ||
          payload.qrcode?.base64 ||
          payload.qrcode?.code ||
          payload.qrcode ||
          payload.code ||
          payload.value ||
          null
        );
      }

      if (payload instanceof ArrayBuffer) {
        const bytes = new Uint8Array(payload);
        const binary = bytes.reduce(
          (acc, byte) => acc + String.fromCharCode(byte),
          ''
        );
        return btoa(binary);
      }

      return null;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || 'Failed to fetch Waha QR code'
        );
      }
      throw new Error('Network error while fetching Waha QR code');
    }
  }

  // ---- Passkey (WebAuthn) — para sessões que o WhatsApp pede passkey ----------
  // Ver browser-extension/ e helper/wahaPasskey.js. Base: /api/{session}/auth.

  static async getPasskeyChallenge(apiUrl, adminToken, sessionName) {
    if (!apiUrl || !adminToken || !sessionName) return null;
    const url = `${normalizeBase(apiUrl)}/${sessionName}/auth/passkey`;
    const response = await axios.get(url, {
      headers: this.requestHeaders(adminToken),
    });
    return response.data; // o desafio WebAuthn, ou null
  }

  static async submitPasskey(apiUrl, adminToken, sessionName, assertion) {
    const url = `${normalizeBase(apiUrl)}/${sessionName}/auth/passkey`;
    await axios.post(url, assertion, {
      headers: this.requestHeaders(adminToken),
    });
  }

  static async getPasskeyConfirmation(apiUrl, adminToken, sessionName) {
    if (!apiUrl || !adminToken || !sessionName) return null;
    const url = `${normalizeBase(apiUrl)}/${sessionName}/auth/passkey/confirmation`;
    const response = await axios.get(url, {
      headers: this.requestHeaders(adminToken),
    });
    return response.data; // { code, skipHandoffUX } ou null
  }

  static async confirmPasskey(apiUrl, adminToken, sessionName) {
    const url = `${normalizeBase(apiUrl)}/${sessionName}/auth/passkey/confirm`;
    await axios.post(url, {}, { headers: this.requestHeaders(adminToken) });
  }
}

export default WahaAPI;
